import React, { useEffect, useCallback, useRef, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useDataApi } from '@adsk/alloy-helpers';
import {
  useCurrentAccountId,
  useCurrentProjectId,
  useAlloyFeatureFlag,
} from '@adsk/alloy-provider';
import { OP_STATUSES } from '@common/constants';
import { useGetProjectTypes } from '@common/hooks';
import {
  withAdminFFs,
  useAdminFeatureFlag,
} from '@common/AdminLaunchDarklyProvider';
import { FLAG_NAMES } from '@common/flagNames';
import {
  isDuplicateNameError,
  buildGetProjectRequest,
  buildEditProjectRequest,
  compareAddresses,
  compareDate,
  getProjectData,
  validProjectValue,
} from './utils';
import { initialState, reducer } from './state/reducer';
import { ACTIONS } from './state/actions';
import EditProjectModalView from './component';

const {
  ADMIN_FLAGS: { ACC_REPLACE_WRITE_CONSTRUCTION_APIS },
} = FLAG_NAMES;

const EditProjectModal = ({ onClose, onSubmit, ...viewProps }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  // Load Admin Feature Flags
  const {
    loading: loadingUseWriteConstructionApis,
    value: useWriteConstructionApis,
  } = useAdminFeatureFlag(ACC_REPLACE_WRITE_CONSTRUCTION_APIS);

  const accountId = useCurrentAccountId();
  const projectId = useCurrentProjectId();
  const { value: additionalFieldsActivated } = useAlloyFeatureFlag(
    'acc-project-profile-additional-fields'
  );
  const {
    data: projectData,
    setData: setProjectData,
    doFetch: doFetchProject,
  } = useDataApi();
  const {
    data: editProjectData,
    error: editProjectError,
    doFetch: doEditProjectFetch,
  } = useDataApi();
  const latestOnSubmit = useRef(onSubmit);
  const { projectTypes, isFetchingProjectTypes } =
    useGetProjectTypes(accountId);

  useEffect(() => {
    if (useWriteConstructionApis == null) return;

    doFetchProject(buildGetProjectRequest(projectId, useWriteConstructionApis));
  }, [doFetchProject, projectId, useWriteConstructionApis]);

  const handleSubmitEditProject = () => {
    dispatch({ type: ACTIONS.SET_IS_SAVING, payload: true });
    dispatch({
      type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
      payload: false,
    });
    const projectDataToUpdate = getProjectData(
      state,
      additionalFieldsActivated
    );
    doEditProjectFetch(
      ...buildEditProjectRequest(projectId, projectDataToUpdate)
    );
  };

  const setMapboxTokenActive = useCallback(
    (active) =>
      dispatch({
        type: ACTIONS.SET_MAPBOX_TOKEN_ACTIVE,
        payload: active,
      }),
    []
  );

  const handleAddressInputModeChange = useCallback(
    (inputMode, resetAddress = false) => {
      dispatch({
        type: ACTIONS.SET_ADDRESS_INPUT_MODE,
        payload: inputMode,
      });
      if (resetAddress) {
        dispatch({ type: ACTIONS.RESET_ADDRESS });
      }
    },
    []
  );

  const handleAutocompletedAddressChange = (autocompletedAddress) => {
    if (autocompletedAddress) {
      dispatch({
        type: ACTIONS.SET_AUTOCOMPLETED_ADDRESS,
        payload: autocompletedAddress,
      });
    } else {
      dispatch({ type: ACTIONS.RESET_ADDRESS });
    }
  };

  const handleAddressChange = (address, resetOldAddress = false) => {
    const newAddress = resetOldAddress
      ? {
          addressLine1: address.addressLine1 ? address.addressLine1 : '',
          addressLine2: address.addressLine2 ? address.addressLine2 : '',
          city: address.city ? address.city : '',
          stateOrProvince: address.stateOrProvince
            ? address.stateOrProvince
            : '',
          postalCode: address.postalCode ? address.postalCode : '',
          country: address.country ? address.country : '',
        }
      : {
          ...state.address,
          ...address,
        };
    dispatch({ type: ACTIONS.SET_ADDRESS, payload: newAddress });
  };

  const handleProjectNameChange = (event) => {
    dispatch({
      type: ACTIONS.SET_PROJECT_NAME,
      payload: event.target.value,
    });

    if (state.showDuplicateNameError) {
      dispatch({
        type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
        payload: false,
      });
    }
  };

  const handleProjectNameBlur = (event) => {
    const projectName = event.target.value;

    if (/^\s|\s$/.test(projectName)) {
      dispatch({
        type: ACTIONS.SET_PROJECT_NAME,
        payload: projectName.trim(),
      });
    }
  };

  const handleProjectTypeChange = (option) => {
    dispatch({
      type: ACTIONS.SET_SELECTED_PROJECT_TYPE,
      payload: option,
    });
  };

  const handleStartDateChange = useCallback((event) => {
    dispatch({ type: ACTIONS.SET_START_DATE, payload: event });
  }, []);

  const handleEndDateChange = useCallback((event) => {
    dispatch({ type: ACTIONS.SET_END_DATE, payload: event });
  }, []);

  const handleCancelClick = useCallback(() => {
    if (state.projectDataChanged) {
      dispatch({
        type: ACTIONS.SET_SHOW_MODAL_CONFIRMATION,
        payload: true,
      });
    } else {
      dispatch({ type: ACTIONS.RESET_EDIT_INPUT_STATE, payload: projectData });
      dispatch({
        type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
        payload: false,
      });
      onClose();
    }
  }, [onClose, state.projectDataChanged, projectData]);

  const handleModalCloseClick = () => handleCancelClick();

  const handleModalConfirmationConfirm = () => {
    dispatch({
      type: ACTIONS.SET_SHOW_MODAL_CONFIRMATION,
      payload: false,
    });
    dispatch({ type: ACTIONS.RESET_EDIT_INPUT_STATE, payload: projectData });
    dispatch({
      type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
      payload: false,
    });
    onClose();
  };

  const handleModalConfirmationCancel = () => {
    dispatch({
      type: ACTIONS.SET_SHOW_MODAL_CONFIRMATION,
      payload: false,
    });
  };

  const handleProjectValueChange = useCallback((event) => {
    if (validProjectValue(event.target.value)) {
      dispatch({
        type: ACTIONS.SET_PROJECT_VALUE,
        payload: event.target.value,
      });
    } else {
      dispatch({
        type: ACTIONS.SET_SHOW_INVALID_PROJECT_VALUE_ERROR,
      });
    }
  }, []);

  const handleJobNumberChange = (event) =>
    dispatch({
      type: ACTIONS.SET_JOB_NUMBER,
      payload: event.target.value,
    });

  const handleTimeZoneChange = (option) =>
    dispatch({
      type: ACTIONS.SET_TIME_ZONE,
      payload: option,
    });

  const handleCurrencyChange = (option) =>
    dispatch({
      type: ACTIONS.SET_CURRENCY,
      payload: option,
    });

  useEffect(() => {
    latestOnSubmit.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    if (!projectData) return;

    dispatch({ type: ACTIONS.RESET_EDIT_INPUT_STATE, payload: projectData });
  }, [projectData]);

  // Edit project success
  useEffect(() => {
    dispatch({ type: ACTIONS.SET_IS_SAVING, payload: false });
    if (editProjectData) {
      latestOnSubmit.current({
        status: OP_STATUSES.SUCCESS,
        response: editProjectData,
      });
      setProjectData({
        ...projectData,
        ...editProjectData,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editProjectData, setProjectData]);

  // Edit project error
  useEffect(() => {
    if (editProjectError === null) return;

    dispatch({ type: ACTIONS.SET_IS_SAVING, payload: false });
    // Intercept and display the duplicate name field error
    if (
      editProjectError.errors &&
      isDuplicateNameError(editProjectError.errors)
    ) {
      dispatch({
        type: ACTIONS.SET_SHOW_DUPLICATED_NAME_ERROR,
        payload: true,
      });
    } else {
      // return other errors
      latestOnSubmit.current({
        status: OP_STATUSES.ERROR,
        response: editProjectError,
      });
    }
  }, [editProjectError]);

  // Set projectDataChanged when fields are updated
  useEffect(() => {
    if (!projectData) return;

    if (
      state.projectName !== projectData.name ||
      state.selectedProjectType.value !== projectData.type ||
      !compareAddresses(projectData, state.address) ||
      !compareDate(state.startDate, projectData.startDate) ||
      !compareDate(state.endDate, projectData.endDate) ||
      (additionalFieldsActivated &&
        (state.jobNumber !== projectData.jobNumber ||
          state.selectedTimeZone?.value !== projectData.timezone ||
          state.projectValue !== projectData.projectValue?.value ||
          state.selectedCurrency.value !== projectData.projectValue.currency))
    ) {
      dispatch({ type: ACTIONS.SET_PROJECT_DATA_CHANGED, payload: true });
    } else {
      dispatch({ type: ACTIONS.SET_PROJECT_DATA_CHANGED, payload: false });
    }
  }, [
    state.projectName,
    state.selectedProjectType,
    state.address,
    state.startDate,
    state.endDate,
    state.jobNumber,
    state.selectedTimeZone,
    state.projectValue,
    state.selectedCurrency,
    projectData,
    additionalFieldsActivated,
  ]);

  // Validate required fields
  useEffect(() => {
    const emptyField = !state.selectedProjectType || !state.projectName;

    if (emptyField !== state.missingRequiredField) {
      dispatch({
        type: ACTIONS.SET_MISSING_REQUIRED_FIELD,
        payload: emptyField,
      });
    }
  }, [
    state.missingRequiredField,
    state.projectName,
    state.selectedProjectType,
  ]);

  if (loadingUseWriteConstructionApis) {
    return null;
  }

  return (
    projectData && (
      <EditProjectModalView
        {...viewProps}
        {...state}
        additionalFieldsActivated={additionalFieldsActivated}
        isFetchingProjectTypes={isFetchingProjectTypes}
        onBlurProjectName={handleProjectNameBlur}
        onChangeAddress={handleAddressChange}
        onChangeAddressInputMode={handleAddressInputModeChange}
        onChangeAutocompletedAddress={handleAutocompletedAddressChange}
        onChangeCurrency={handleCurrencyChange}
        onChangeEndDate={handleEndDateChange}
        onChangeJobNumber={handleJobNumberChange}
        onChangeProjectName={handleProjectNameChange}
        onChangeProjectType={handleProjectTypeChange}
        onChangeProjectValue={handleProjectValueChange}
        onChangeStartDate={handleStartDateChange}
        onChangeTimeZone={handleTimeZoneChange}
        onClickCancel={handleCancelClick}
        onCloseModal={handleModalCloseClick}
        onModalConfirmationCancel={handleModalConfirmationCancel}
        onModalConfirmationConfirm={handleModalConfirmationConfirm}
        onSubmitEditProject={handleSubmitEditProject}
        project={projectData}
        projectTypes={projectTypes}
        setMapboxTokenActive={setMapboxTokenActive}
      />
    )
  );
};

EditProjectModal.propTypes = {
  /** Required Access token for the Mapbox address component.
   * If not specified, defaults to manual input flow */
  mapBoxAccessToken: PropTypes.string.isRequired,
  /**  handler executed when the user clicks on the Cancel button or on the Close Dialog cross */
  onClose: PropTypes.func.isRequired,
  /** handler executed when the cuser clicks on the Create Project button */
  onSubmit: PropTypes.func.isRequired,
  /** Boolean property to set the visibility of the Modal */
  show: PropTypes.bool,
};

EditProjectModal.defaultProps = {
  show: false,
};

export default withAdminFFs(EditProjectModal);
